import React from "react";
import axios from "axios";
import { Howl } from "howler";
import getConfig from "next/config";
import axiosRetry from "axios-retry";
import { captureMessage, captureException } from "@sentry/nextjs";
import { Props as NotificationData } from "../Widget";

axiosRetry(axios, {
  retryCondition: (error) => {
    return true;
  },
});

const {
  publicRuntimeConfig: { buzzerApiUrl },
} = getConfig();

export type NotificationEvent = {
  data: NotificationData;
  receipt: string;
  alertId: string;
};

const loadAudio = (url: string, volume?: number): Promise<Howl | null> =>
  new Promise((resolve) => {
    const audio = new Howl({
      src: url.endsWith(".ogg") ? [url, url.replace(/\.ogg$/, ".mp3")] : url,
      volume: volume,
      html5: true,
    });

    if (audio.state() === "loaded") {
      resolve(audio);
      return;
    }

    audio.once("load", () => resolve(audio));
    audio.once("loaderror", () => resolve(null));
  });

const loadAudios = async (
  notification: NotificationData,
): Promise<(Howl | null)[]> => {
  const tasks = [];

  if (notification.config.audioEnabled && notification.config.audioUrl) {
    const volume = ((notification.config.audioVolume as number) || 50) / 100;
    tasks.push(loadAudio(notification.config.audioUrl as string, volume));
  } else {
    tasks.push(null);
  }

  if (
    notification.config.textToSpeechEnabled &&
    notification.config.textToSpeechUrl
  ) {
    const volume =
      ((notification.config.textToSpeechVolume as number) || 50) / 100;
    tasks.push(
      loadAudio(notification.config.textToSpeechUrl as string, volume),
    );
  } else {
    tasks.push(null);
  }

  return Promise.all(tasks);
};

const useNotifications = () => {
  const [visible, setVisible] = React.useState<boolean>(false);
  const [willDisappear, setWillDisappear] = React.useState<boolean>(false);

  const [event, setEvent] = React.useState<NotificationEvent>();

  const showNotification = React.useCallback(
    (receivedEvent: NotificationEvent) => {
      if (window.busy) {
        console.log(`Skipping notification ${receivedEvent.alertId} (busy)`);
        return;
      }

      console.log(`Showing notification ${receivedEvent.alertId}`);

      if (window.notificationTimeout) {
        clearTimeout(window.notificationTimeout);
        window.notificationTimeout = undefined;
      }

      window.busy = true;
      window.notificationEvent = receivedEvent;
      window.notificationTimeout = setTimeout(() => {
        captureMessage("Notification Timeout", {
          extra: receivedEvent,
        });

        console.error(`Timeout notification ${receivedEvent.alertId}`);

        window.busy = false;
        window.notificationEvent = undefined;
      }, 30_000);

      setEvent(undefined);

      setTimeout(() => {
        setEvent(receivedEvent);
      }, 100);
    },
    [],
  );

  const hideNotification = React.useCallback((delay: number = 2_000) => {
    if (!window.notificationEvent) {
      window.busy = false;
      return;
    }

    setTimeout(() => {
      if (window.notificationDispose) window.notificationDispose();
      window.notificationDispose = undefined;

      if (!window.notificationEvent) {
        window.busy = false;
        return;
      }

      console.log(`Hiding notification ${window.notificationEvent.alertId}`);

      setWillDisappear(true);

      let attempts = 0;

      const confirm = () => {
        axios
          .post<NotificationEvent>(
            `${buzzerApiUrl}/alerts/${window.notificationEvent.alertId}`,
            {},
            {
              headers: {
                "x-receipt": window.notificationEvent.receipt,
              },
              timeout: 10_000,
            },
          )
          .then((response) => {
            window.busy = false;
            window.notificationEvent = undefined;

            setVisible(false);
            setWillDisappear(false);
            setEvent(undefined);

            if (response.status === 200 && response.data) {
              showNotification(response.data);
            }
          })
          .catch((e) => {
            if (attempts >= 3) {
              window.busy = false;
              window.notificationEvent = undefined;

              setVisible(false);
              setWillDisappear(false);
              setEvent(undefined);

              captureException(e);
            } else {
              attempts++;
              setTimeout(confirm, 1_000);
            }
          });
      };

      confirm();
    }, delay);
  }, []);

  const playNotification = React.useCallback(async () => {
    if (!event) return;

    console.log(`Playing notification ${event.alertId}`);

    if (window.notificationTimeout) {
      clearTimeout(window.notificationTimeout);
      window.notificationTimeout = undefined;
    }

    try {
      const defaultDuration = Number(event.data.config.minimumDuration) * 1_000;
      const notification = event.data;

      const timeout = setTimeout(
        () => hideNotification(0),
        Number(event.data.config.maximumDuration) * 1_000,
      );

      const [audio, speech] = await loadAudios(notification);

      setVisible(true);

      window.notificationDispose = () => {
        console.log(`Disposing notification ${event.alertId}`);

        if (audio) {
          audio.stop();
          audio.unload();
        }

        if (speech) {
          speech.stop();
          speech.unload();
        }

        clearTimeout(timeout);
      };

      if (audio) {
        const duration = (notification.config.audioDuration as number) * 1_000;

        audio.once("playerror", console.error);
        audio.play();

        if (speech) {
          setTimeout(() => {
            audio.fade(audio.volume(), 0, 1_000);

            speech.once("playerror", () => hideNotification(0));
            speech.once("end", () => hideNotification(1_000));

            speech.play();
          }, duration);
        } else {
          setTimeout(
            () => {
              audio.fade(audio.volume(), 0, 1_000);

              hideNotification(1_000);
            },
            Math.max(duration, defaultDuration),
          );
        }
      } else if (speech) {
        speech.once("playerror", () => hideNotification(0));
        speech.once("end", () => hideNotification(1_000));

        speech.play();
      } else {
        hideNotification(defaultDuration);
      }
    } catch (e) {
      console.log(e);
      captureException(e);
      hideNotification(0);
    }
  }, [event]);

  return {
    notification: event?.data,
    identifier: event?.alertId,
    visible,
    willDisappear,
    showNotification,
    hideNotification,
    playNotification,
  };
};

export default useNotifications;
